/* boostCoverItems Component */

/* Create UI based on the boost details API response */
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { isUSRegionSelector, useRegionStore } from '@packages/stores';
import { BoostCoverItem } from '@packages/types';

import { Button } from '@elements/button';
import { TextWithMd } from '@elements/text/textWithMd';
import { i18n } from '@i18n';
import { sendAnalyticsEvent, sluggify, testProps } from '@utils';

import { Text } from '../text';
import { BulletPointsItem } from './bulletPointsItem.component';
import { SheetTableList } from './sheetTableList.component';
import { TableItem } from './tableItem.component';

export interface BoostCoverItemsProps extends React.HTMLAttributes<HTMLDivElement> {
  isCovered?: boolean;
  boostCoverItems?: BoostCoverItem[] | null;
  isMandatory?: boolean;
  pdsLink?: string;
}
export const BoostCoverItems: React.FC<BoostCoverItemsProps> = ({
  boostCoverItems,
  isCovered,
  className,
  isMandatory,
  pdsLink,
  ...rest
}) => {
  const isUS = useRegionStore(isUSRegionSelector);
  if (!boostCoverItems || boostCoverItems.length === 0) {
    return null;
  }

  //Dividing items into two columns for large screens
  let leftColList: BoostCoverItem[] = [];
  let rightColList: BoostCoverItem[] = [];

  if (isMandatory && !isUS) {
    if (isCovered) {
      leftColList = boostCoverItems.filter(item => item.type == 'sheet-table');
      rightColList = boostCoverItems.filter(item => item.type != 'sheet-table');
    } else {
      const boostCoverItemListLength = boostCoverItems.length;
      leftColList = boostCoverItems.slice(0, boostCoverItemListLength / 2);
      rightColList = boostCoverItems.slice(boostCoverItemListLength / 2, boostCoverItemListLength);
      const leftColListLength = leftColList.length;
      rightColList.unshift(leftColList[leftColListLength - 1]);
      leftColList = leftColList.slice(0, leftColListLength - 1);
    }
  } else {
    //No "bullet" and "text" type as the first item of the right column
    const boostCoverItemListLength = boostCoverItems.length;
    leftColList = boostCoverItems.slice(0, boostCoverItemListLength / 2);
    rightColList = boostCoverItems.slice(boostCoverItemListLength / 2, boostCoverItemListLength);
    while (rightColList[0].type === 'text' || rightColList[0].type === 'bullet') {
      leftColList.push(rightColList[0]);
      rightColList = rightColList.slice(1);
    }
  }

  const onPdsLinkClick = () => {
    if (!pdsLink) {
      return;
    }
    sendAnalyticsEvent('Read our PDS Clicked');
    window.open(pdsLink, '_blank', 'noopener,noreferrer');
  };

  const testPropsName = isCovered ? 'what-is-cover-button' : 'what-is-not-cover-button';

  const createItems = (list: BoostCoverItem[]) => {
    return list.map((item, index) => {
      const itemType = item.type;
      switch (itemType) {
        case 'bold':
          return (
            <div className="flex-1 pb-s12" key={`${itemType}${index}`}>
              <Text variant="h4-24/sb" {...testProps(sluggify(item.value))}>
                {item.value}
              </Text>
            </div>
          );
        case 'text':
          return (
            <TextWithMd
              className="pb-s16"
              variant="body-16/r"
              key={`${itemType}${index}`}
              {...testProps(sluggify(item.value))}>
              {item.value}
            </TextWithMd>
          );
        case 'table':
          return <TableItem key={`${itemType}${index}`} item={item} />;
        case 'bullet':
          return <BulletPointsItem key={`${itemType}${index}`} item={item} isCovered={isCovered} />;
        case 'sheet-table':
          return <SheetTableList key={`${itemType}${index}`} item={item} />;
        default:
          return null;
      }
    });
  };

  return (
    <div className={twMerge('flex gap-x-8 flex-col md:flex-row', className)} {...rest}>
      <div className="w-full">{createItems(leftColList)}</div>
      <div className="w-full">
        {createItems(rightColList)}
        {pdsLink && (
          <Button
            {...testProps(testPropsName)}
            onClick={onPdsLinkClick}
            variant="secondary"
            title={i18n.t('boostDetails.pdsButton')}
          />
        )}
      </div>
    </div>
  );
};
